import { PageHero, LocalBusinessSchema } from '@components/common';
import { CALENDLY_URL } from '@constants';
import { Shield, Target, Users, Clock, MapPin } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function CardiffPage() {
  return (
    <>
      <LocalBusinessSchema city="Cardiff" latitude={51.4816} longitude={-3.1791} />
      <PageHero
        title="Fractional CISO Services in Cardiff"
        subtitle="Cybersecurity leadership for Wales' thriving digital economy"
        eyebrow="Cardiff"
        seoTitle="Fractional CISO Cardiff | The CISO Network"
        seoDescription="Expert fractional CISO services for Cardiff and Welsh businesses. Access senior cybersecurity leadership for your growing tech company."
      />

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Securing Cardiff's Digital Transformation
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Cardiff has emerged as one of the UK's fastest-growing digital capitals, with a
                  thriving ecosystem spanning fintech, creative industries, cybersecurity, and
                  government digital services. The city's Cardiff Bay tech cluster and city centre
                  innovation hubs are home to hundreds of ambitious businesses driving Wales'
                  digital economy forward.
                </p>
                <p>
                  Welsh businesses face unique cybersecurity challenges. The region's growing
                  fintech sector must navigate complex FCA regulations, while creative agencies
                  handle valuable intellectual property. Public sector contractors face stringent
                  security requirements, and the emerging cybersecurity sector demands cutting-edge
                  expertise. Many Cardiff businesses also serve UK-wide clients from their Welsh
                  base, requiring enterprise-grade security.
                </p>
                <p>
                  Despite this growth, accessing senior cybersecurity talent in Wales can be
                  challenging. Many businesses find London-based CISOs too expensive or remote,
                  while local talent is in high demand. Our fractional CISO service bridges this
                  gap, providing Cardiff businesses with executive-level security leadership that
                  understands the Welsh business landscape.
                </p>
                <p>
                  Cardiff's economy is built on public administration, energy, life sciences, and
                  emerging technology sectors. Welsh organisations face the dual challenge of
                  UK-wide regulations and devolved authority requirements. A fractional CISO with
                  experience in Welsh public sector and commercial environments can navigate this
                  landscape efficiently.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Your Account Manager
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Your account manager understands the unique Welsh business environment, from
                      Cardiff Bay to the Valleys, ensuring locally relevant CISO support.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Clock className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Wales-Wide Coverage
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Supporting businesses across Cardiff, Newport, Swansea, and throughout Wales
                      with flexible on-site and remote CISO services.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Welsh Businesses Choose Fractional CISOs
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Enterprise-grade security leadership accessible to Wales' growing businesses
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Public Sector Ready',
                description:
                  'Meet the security standards required for Welsh Government and UK public sector contracts.',
              },
              {
                icon: Target,
                title: 'Cost Effective',
                description:
                  "Access C-level expertise without Cardiff's rising senior security salary costs.",
              },
              {
                icon: Users,
                title: 'Local Knowledge',
                description:
                  'CISOs who understand Welsh industries, from fintech to creative to government.',
              },
              {
                icon: Clock,
                title: 'Flexible Support',
                description: 'Scale your CISO engagement as your Welsh business grows and evolves.',
              },
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <benefit.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO Services in Cardiff - FAQs"
        subtitle="Common questions from Cardiff and Welsh businesses"
        items={[
          {
            id: 'cardiff-1',
            question: 'How does a fractional CISO work with businesses in Cardiff?',
            answer:
              'Our fractional CISOs work with organisations across the UK, including Cardiff. They can attend meetings on-site or work remotely, depending on what suits your business. We match you with a CISO who understands the security challenges facing public sector, energy, and life sciences companies.',
          },
          {
            id: 'cardiff-2',
            question: 'What size of business benefits from a fractional CISO in Cardiff?',
            answer:
              'Fractional CISO services are ideal for growing businesses that need strategic security leadership but are not yet ready for a full-time executive hire. This includes Cardiff startups, scale-ups, and mid-sized companies that are winning larger contracts or facing regulatory requirements.',
          },
          {
            id: 'cardiff-3',
            question: 'What industries do your CISOs cover in Cardiff?',
            answer:
              "Our network includes CISOs with experience in public sector, energy, and life sciences. We match you with someone who understands your sector's specific regulatory environment and threat landscape, whether you are navigating FCA requirements, NHS Digital standards, or customer security questionnaires.",
          },
          {
            id: 'cardiff-4',
            question: 'What is the first thing a fractional CISO does when they start?',
            answer:
              'Every engagement begins with discovery. Your CISO assesses your current security posture, understands your business priorities, and develops a tailored roadmap. This typically delivers actionable insights within the first 30 days, with quick wins identified early.',
          },
          {
            id: 'cardiff-5',
            question: 'Can a fractional CISO help with board reporting and governance?',
            answer:
              'Yes. A core part of the fractional CISO role is translating technical security risks into business language for boards and leadership teams. Our CISOs build reporting frameworks, present to boards, and establish governance structures that satisfy regulators and investors.',
          },
        ]}
      />

      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Secure Your Cardiff Business?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Speak with our team today and discover how a fractional CISO can protect your digital
            growth.
          </p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
            Book a Call
          </a>
        </div>
      </section>
    </>
  );
}
